import React, { useCallback, useEffect, useState } from 'react';
import { Card, IndexTable, Badge, Button, InlineStack, Modal, Text, BlockStack, EmptySearchResult, Spinner } from '@shopify/polaris';

const RESULT_ORDER = ['missing', 'unexpected', 'seen', 'foreign'];
const RESULT_TONE = { missing: 'critical', unexpected: 'warning', seen: 'success', foreign: undefined };

export default function CountsTab({ api, warehouse, onOpenBox, toast }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);         // {run, items}
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const j = await api('/api/rfid/count-runs?limit=50'); setRuns(j.runs || []); }
    catch (e) { toast('err', e.message); }
    finally { setLoading(false); }
  }, [api, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!openId) { setOpen(null); return; }
    let alive = true;
    api(`/api/rfid/count-runs/${openId}`).then(j => alive && setOpen(j)).catch(e => { if (!alive) return; toast('err', e.message); setOpenId(null); });
    return () => { alive = false; };
  }, [openId, api, toast]);

  const scope = (r) => r.location_code || (r.zone ? `zone ${r.zone}` : '—');

  return (
    <Card>
      <InlineStack gap="300" blockAlign="center">
        <Button onClick={load} loading={loading}>Refresh</Button>
        <Text as="span" tone="subdued">Last {runs.length} count runs at {warehouse?.name}</Text>
      </InlineStack>
      <div style={{ height: 12 }} />
      <IndexTable
        resourceName={{ singular: 'count run', plural: 'count runs' }}
        itemCount={runs.length}
        selectable={false}
        loading={loading}
        emptyState={<EmptySearchResult title="No count runs yet" description="Counts are started from the handheld." withIllustration />}
        headings={[{ title: 'Started' }, { title: 'Scope' }, { title: 'By' }, { title: 'Status' }, { title: 'Expected' }, { title: 'Seen' }, { title: 'Missing' }, { title: 'Unexpected' }]}
      >
        {runs.map((r, i) => (
          <IndexTable.Row id={String(r.id)} key={r.id} position={i} onClick={() => setOpenId(r.id)}>
            <IndexTable.Cell>{new Date(r.started_at).toLocaleString()}</IndexTable.Cell>
            <IndexTable.Cell>{scope(r)}</IndexTable.Cell>
            <IndexTable.Cell>{r.started_by || r.device_id || '—'}</IndexTable.Cell>
            <IndexTable.Cell><Badge tone={r.status === 'closed' ? undefined : 'info'}>{r.status}</Badge></IndexTable.Cell>
            <IndexTable.Cell>{r.expected ?? '—'}</IndexTable.Cell>
            <IndexTable.Cell>{r.seen ?? '—'}</IndexTable.Cell>
            <IndexTable.Cell>{r.missing ? <Badge tone="critical">{String(r.missing)}</Badge> : (r.missing ?? '—')}</IndexTable.Cell>
            <IndexTable.Cell>{r.unexpected ? <Badge tone="warning">{String(r.unexpected)}</Badge> : (r.unexpected ?? '—')}</IndexTable.Cell>
          </IndexTable.Row>
        ))}
      </IndexTable>

      <Modal open={!!openId} onClose={() => setOpenId(null)} title={open ? `Count run #${open.run.id} · ${scope(open.run)}` : 'Count run'} size="large">
        <Modal.Section>
          {!open && <InlineStack align="center"><Spinner size="small" /></InlineStack>}
          {open && (
            <BlockStack gap="400">
              <InlineStack gap="300">
                <Text as="span">Status: <b>{open.run.status}</b></Text>
                <Text as="span">Expected {open.run.expected ?? '—'} · seen {open.run.seen ?? '—'} · missing {open.run.missing ?? '—'} · unexpected {open.run.unexpected ?? '—'}</Text>
              </InlineStack>
              {RESULT_ORDER.map(result => {
                const items = (open.items || []).filter(it => it.result === result);
                if (!items.length) return null;
                return (
                  <BlockStack gap="100" key={result}>
                    <InlineStack gap="200" blockAlign="center">
                      <Badge tone={RESULT_TONE[result]}>{result}</Badge>
                      <Text as="span" tone="subdued">{items.length}</Text>
                    </InlineStack>
                    {items.map((it, i) => (
                      <div className="overstock-history-row overstock-count-row" key={`${result}-${i}`}>
                        <span className="overstock-mono">{it.epc}</span>
                        <span>{it.box_id ? `#${it.box_id} · ${it.sku || ''} ${it.title || ''}` : 'not one of our boxes'}</span>
                        <span>{it.box_id ? <Button size="slim" onClick={() => onOpenBox(it.box_id)}>Open box</Button> : (it.rssi != null ? `${it.rssi} dBm` : '')}</span>
                      </div>
                    ))}
                  </BlockStack>
                );
              })}
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Card>
  );
}
